// Leaderboard.js - Friends leaderboard component
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../Services/Api';
import styles from './Leaderboard.module.css';

function Leaderboard({ userData }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [sortBy, setSortBy] = useState('current_streak');
  const [totalFriends, setTotalFriends] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, [sortBy]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await api.get(`leaderboard/?sort_by=${sortBy}`);
      setLeaderboard(response.data.leaderboard);
      setTotalFriends(response.data.total_friends);
      setError(null);
    } catch (err) {
      setError('Failed to load leaderboard');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRankEmoji = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return `#${rank}`;
    }
  };

  const getRankClass = (rank) => {
    switch (rank) {
      case 1: return styles.gold;
      case 2: return styles.silver;
      case 3: return styles.bronze;
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>🏆 Leaderboard</h1>
        <p className={styles.subtitle}>Compete with friends and stay motivated!</p>
      </div>

      {error && (
        <div className={styles.error}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Sort Options */}
      <div className={styles.controls}>
        <div className={styles.sortButtons}>
          <button
            className={`${styles.sortButton} ${sortBy === 'current_streak' ? styles.activeSort : ''}`}
            onClick={() => setSortBy('current_streak')}
          >
            🔥 Current Streak
          </button>
          <button
            className={`${styles.sortButton} ${sortBy === 'longest_streak' ? styles.activeSort : ''}`}
            onClick={() => setSortBy('longest_streak')}
          >
            🏆 Best Streak
          </button>
        </div>
        <div className={styles.friendCount}>
          👥 {totalFriends} friend{totalFriends !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Leaderboard */}
      <div className={styles.leaderboardContainer}>
        {leaderboard.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>🏆</span>
            <h3>No one on the leaderboard yet</h3>
            <p>Add friends to start competing!</p>
            <Link to="/friends" className={styles.addFriendsButton}>
              Find Friends
            </Link>
          </div>
        ) : (
          <>
            {/* Top 3 Podium */}
            {leaderboard.length >= 3 && (
              <div className={styles.podium}>
                {/* Second Place */}
                <div className={`${styles.podiumPlace} ${styles.second}`}>
                  <div className={styles.podiumAvatar}>
                    {leaderboard[1].username.charAt(0).toUpperCase()}
                  </div>
                  <span className={styles.podiumRank}>🥈</span>
                  <span className={styles.podiumName}>
                    {leaderboard[1].username}
                    {leaderboard[1].is_current_user && <span className={styles.youBadge}>You</span>}
                  </span>
                  <span className={styles.podiumStreak}>
                    {sortBy === 'current_streak' 
                      ? `${leaderboard[1].current_streak} days`
                      : `${leaderboard[1].longest_streak} days`
                    }
                  </span>
                </div>

                {/* First Place */}
                <div className={`${styles.podiumPlace} ${styles.first}`}>
                  <div className={styles.podiumAvatar}>
                    {leaderboard[0].username.charAt(0).toUpperCase()}
                  </div>
                  <span className={styles.podiumRank}>🥇</span>
                  <span className={styles.podiumName}>
                    {leaderboard[0].username}
                    {leaderboard[0].is_current_user && <span className={styles.youBadge}>You</span>}
                  </span>
                  <span className={styles.podiumStreak}>
                    {sortBy === 'current_streak' 
                      ? `${leaderboard[0].current_streak} days`
                      : `${leaderboard[0].longest_streak} days`
                    }
                  </span>
                </div>

                {/* Third Place */}
                <div className={`${styles.podiumPlace} ${styles.third}`}>
                  <div className={styles.podiumAvatar}>
                    {leaderboard[2].username.charAt(0).toUpperCase()}
                  </div>
                  <span className={styles.podiumRank}>🥉</span>
                  <span className={styles.podiumName}>
                    {leaderboard[2].username}
                    {leaderboard[2].is_current_user && <span className={styles.youBadge}>You</span>}
                  </span>
                  <span className={styles.podiumStreak}>
                    {sortBy === 'current_streak' 
                      ? `${leaderboard[2].current_streak} days`
                      : `${leaderboard[2].longest_streak} days`
                    }
                  </span>
                </div>
              </div>
            )}

            {/* Full Rankings List */}
            <div className={styles.rankingsList}>
              {leaderboard.map((entry, index) => (
                <div 
                  key={entry.user_id} 
                  className={`${styles.rankingCard} ${getRankClass(entry.rank)} ${entry.is_current_user ? styles.currentUser : ''}`}
                >
                  <div className={styles.rankNumber}>
                    {getRankEmoji(entry.rank)}
                  </div>
                  <div className={styles.rankAvatar}>
                    {entry.username.charAt(0).toUpperCase()}
                  </div>
                  <div className={styles.rankInfo}>
                    <h4>
                      {entry.username}
                      {entry.is_current_user && <span className={styles.youTag}>(You)</span>}
                    </h4>
                    <div className={styles.rankStats}>
                      <span className={styles.currentStreak}>
                        🔥 {entry.current_streak} days
                      </span>
                      <span className={styles.separator}>•</span>
                      <span className={styles.bestStreak}>
                        🏆 Best: {entry.longest_streak}
                      </span>
                    </div>
                  </div>
                  <div className={styles.streakValue}>
                    {sortBy === 'current_streak' 
                      ? entry.current_streak
                      : entry.longest_streak
                    }
                    <span className={styles.daysLabel}>days</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Motivational Footer */}
      {leaderboard.length > 0 && (
        <div className={styles.motivationalFooter}>
          <p>💪 Every day clean is a victory. Keep pushing!</p>
        </div>
      )}
    </div>
  );
}

export default Leaderboard;
